import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { toast } from "sonner";
import { Plus, Search, Edit, Archive, Star } from "lucide-react";

export default function AdminCatalogue() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  useEffect(() => { document.title = "Catalogue | Sambiva Admin"; }, []);

  const { data: products = [] } = useQuery({ queryKey: ["admin-products"], queryFn: async () => (await api.get("/admin/products")).data });

  const archive = async (p) => {
    if (!window.confirm(`Archive ${p.name}?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    qc.invalidateQueries({ queryKey: ["admin-products"] });
    toast.success("Product archived.");
  };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()) || p.sku.toLowerCase().includes(search.toLowerCase()));

  return (
    <AdminLayout title="Catalogue">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-sambiva-muted" size={18} />
          <input data-testid="admin-product-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…" className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-black/10 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sambiva-green/40" />
        </div>
        <button data-testid="admin-new-product" onClick={() => navigate("/admin/catalogue/new")} className="flex items-center gap-2 bg-sambiva-green hover:bg-sambiva-deepgreen text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition-colors"><Plus size={18} /> New Product</button>
      </div>

      <div className="bg-white rounded-2xl border border-black/5 overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-[#f6f7f9] text-sambiva-muted text-left">
            <tr><th className="px-4 py-3 font-semibold">Product</th><th className="px-4 py-3 font-semibold">SKU</th><th className="px-4 py-3 font-semibold">Category</th><th className="px-4 py-3 font-semibold">Trade Price</th><th className="px-4 py-3 font-semibold">Stock</th><th className="px-4 py-3 font-semibold">Status</th><th className="px-4 py-3 font-semibold text-right">Actions</th></tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} data-testid={`admin-product-${p.sku}`} className="border-t border-black/5 hover:bg-sambiva-cream/30">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img src={p.images?.[0]} alt="" className="w-10 h-10 rounded-lg object-cover" />
                    <span className="font-medium text-sambiva-charcoal flex items-center gap-1">{p.name} {p.featured && <Star size={13} className="text-sambiva-yellow fill-sambiva-yellow" />}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sambiva-muted">{p.sku}</td>
                <td className="px-4 py-3 text-sambiva-muted">{p.category}</td>
                <td className="px-4 py-3 font-medium text-sambiva-green">€{p.trade_price.toFixed(2)}</td>
                <td className="px-4 py-3"><span className={p.stock_quantity <= p.low_stock_threshold ? "text-orange-600 font-semibold" : "text-sambiva-charcoal"}>{p.stock_quantity}</span></td>
                <td className="px-4 py-3">{p.active ? <span className="text-xs font-semibold text-sambiva-green">Active</span> : <span className="text-xs font-semibold text-sambiva-muted">Archived</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid={`edit-${p.sku}`} onClick={() => navigate(`/admin/catalogue/${p.id}`)} className="p-2 rounded-lg hover:bg-sambiva-cream text-sambiva-charcoal"><Edit size={16} /></button>
                    <button data-testid={`archive-${p.sku}`} onClick={() => archive(p)} className="p-2 rounded-lg hover:bg-sambiva-red/10 text-sambiva-red"><Archive size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
